<template>
  <b-form v-if="!loading" @submit="onSubmit" novalidate class="invoice-wrap gk-input-light--delete">

    <div class="invoice-section">
      <div class="invoice-section--label">{{ $gettext('Invoice details') }}</div>
      <div class="is-shadow-box p-3">
        <b-form-row class="gk-input-light--delete">
          
          <!-- Invoice number (auto generated) -->
          <b-col cols="12" lg="6">
            <b-form-group label-class="gk-label-margin gk-modal-label" :label="$gettext('Invoice number*')" label-for="inv-upload-number">
              <b-form-input data-analytics-click id="inv-upload-number" v-model="invoice.number" @input="() => this.errors = {}" :state="isValid('invoice_number')" required :placeholder="'2023-0000001'" />
              <b-form-invalid-feedback :state="isValid('invoice_number')">
                <translate v-if="!errors.number">This field is required.</translate>
                <div v-for="(error, index) in errors.number" :key="index">{{error}}</div>
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>

          <!-- Invoice date -->
          <b-col cols="12" lg="6">
            <b-form-group label-class="gk-label-margin gk-modal-label" :label="$gettext('Invoice date*')" label-for="inv-upload-date">
              <b-form-datepicker 
                id="inv-upload-date"
                class="inline-form-datepicker"
                v-model="invoice.invoice_date" no-flip
                :date-format-options="{ year: 'numeric', month: 'short', day: 'numeric' }"
                @input="updateDate"
              />
            </b-form-group>
          </b-col>

          <!-- Invoice customer -->
          <b-col cols="12" lg="6">
            <b-form-group label-class="gk-label-margin gk-modal-label" label="Customer" label-for="inv-upload-customer">
              <CustomerSelect 
                ref="customer-selector"
                id="inv-upload-customer"
                :preselect="preselectedCustomer"
                searchable
                @selected-customer="selectedCustomer"
                @pre-selected-customer="selectedCustomer"
              />
              <b-form-invalid-feedback :state="isValid('recipient_customer')">
                {{ $gettext('This field is required.') }}
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>

          <!-- Invoice project (customer selected) -->
          <b-col cols="12" lg="6">
            <b-form-group label-class="gk-label-margin gk-modal-label" :label="$gettext('Project')" label-for="inv-upload-project">
              <b-form-select v-model="invoice.project" :disabled="projectList.length === 0" id="inv-upload-project">
                <b-form-select-option v-for="project in projectList" :value="project.id" :key="project.id">
                  {{ project.name }}
                </b-form-select-option>
              </b-form-select>
            </b-form-group>
          </b-col>

          <!-- Invoice payment status -->
          <b-col cols="12" lg="6">
            <b-form-group label-class="gk-label-margin gk-modal-label" :label="$gettext('Payment type')" label-for="inv-upload-paid">
              <PaymentTypeSelector 
                id="inv-upload-paid"
                is-invoice
                v-model="payment.payment_type"
                @change="updatePaymentType"
                :disabled="initialStatus === 'p'"
              />
            </b-form-group>
          </b-col>

          <!-- Invoice payment term -->
          <b-col cols="12" lg="6" :class="{ 'd-none': initialStatus === 'p' }">
            <b-form-group label-class="gk-label-margin gk-modal-label" :label="$gettext('Payment term*')" label-for="inv-upload-payment_term">
              <b-input-group>
                <template #append>
                  <b-input-group-text>{{ $gettext('days') }}</b-input-group-text>
                </template>
                <b-form-input :disabled="initialStatus === 'p'" data-analytics-click id="inv-upload-payment_term" v-model="invoice.payment_term" :state="isValid('invoice_payment-term')" required :placeholder="(currentCompany.default_payment_term).toString()" />
              </b-input-group>
              <b-form-invalid-feedback :state="isValid('invoice_payment-term')">
                {{ $gettext('This field is required.') }}
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>

          <b-col cols="12" lg="6">
            <b-form-group 
              label-class="gk-label-margin gk-modal-label" 
              :label="$gettext('Status')" 
              label-for="inv-upload-status"
              v-b-tooltip="{ trigger: 'hover', delay: { show: 500 } }"
              :title="isEdit && initialStatus != 'd' ? $gettext('Once the invoice is marked as sent or paid, its status can no longer be changed.') : null"
            >
              <b-form-select
                v-model="invoice.status"
                :disabled="isEdit && ['s', 'p'].includes(initialStatus) || payment.payment_type != 'notpaid'"
                @change="changeInvoiceStatus"
                id="inv-upload-status"
              >
                <b-form-select-option v-for="status in statusOptions" :value="status.id" :key="status.id">
                  {{ status.name }}
                </b-form-select-option>
              </b-form-select>
            </b-form-group>
            <b-alert 
              v-if="(!isEdit || (isEdit && initialStatus === 'd')) && ['s', 'p'].includes(invoice.status)" 
              show class="gk-alert m-0" style="font-size: 1rem;" variant="warning">
              {{ $gettext('If you mark the invoice as sent or paid, the status can no longer be changed.') }}
            </b-alert>
          </b-col>
        </b-form-row>
      </div>
    </div>

    <div class="invoice-section">
      <div class="invoice-section--lines">
        <div class="invoice-section--label">{{ $gettext('Invoice lines') }}</div>
        <div class="invoice-lines-wrap is-shadow-box">
          <InvoiceLines 
            v-model="invoice.invoice_lines"
            :invoice="invoice"
            :show-error="isValid('invoice_lines') === false"
            ref="invoice-lines"
          />
        </div>
      </div>
    </div>

    <div v-if="!isEdit" class="invoice-section">
      <div class="invoice-section--label">{{ $gettext('Upload invoice file') }}</div>
      <div class="p-3 is-shadow-box">
        <LoadingSpinner v-if="uploadingFiles" :display-text="$gettext('Uploading invoice. This might take a while.')" />
        <div style="margin-bottom: 1rem; font-size: 1rem; display: flex; justify-content: space-between;">
          <div>{{ $gettext('File* (only 1 allowed)') }}</div>
          <div>
            {{ $gettext('Total file size:') }} <span :class="{ 'text-danger': maxSizeExceeded }">{{ niceBytes(totalFileSize, 1000, true) }}</span> / {{ niceBytes(maxTotalFileSize*1000*1000, 1000, true) }}
          </div>
        </div>
        
        <FileUploader
          ref="file-uploader"
          showsize
          :max-files="1"
          :file-size-max="maxTotalFileSize"
          @updated-queued-files="updateInvoiceFiles"
        />
        <div v-if="maxSizeExceeded" class="text-danger" style="font-size: 14px; padding: 0.5rem; margin-top: 1rem;">
          {{ $gettextInterpolate(maxSizeExceededText, { size: niceBytes(totalFileSize, 1000, true), max: maxTotalFileSize }) }}
        </div>
        <div v-if="!isValid('invoice_file')" class="text-danger" style="font-size: 14px; padding: 0.5rem; margin-top: 1rem;">
          {{ $gettext('This field is required.') }}
        </div>
      </div>
    </div>

    <div class="invoice-section">
      <GekkoButton 
        v-if="!emitSubmit"
        type="submit"
        :text="$gettext('Add correction')" 
        icon="fa-solid fa-plus"
        :disabled="isSaving"
        class="float-right position-relative"
        :loading="isWaitingForResponse"
      />
    </div>

  </b-form>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from "vuex";
import { niceBytes } from "@/utils/";
import GekkoButton from "@/components/GekkoButton";
import FileUploader from "@/components/ui/FileUploader";
import LoadingSpinner from "@/components/dashboard/ui/LoadingSpinner"
import CustomerSelect from "@/components/invoices/ui/CustomerSelect"

import PeriodSelector from "@/components/ui/PeriodSelector";
import InvoiceLines from "@/components/invoices/ui/InvoiceLines";
import PaymentTypeSelector from "@/components/costs/PaymentTypeSelector";

import CustomerService from "@/store/services/customer.service.js";
import InvoiceUploadService from "@/store/services/invoice.upload.service.js";
import InvoicePaymentsService from "@/store/services/invoice.payments.service.js";
import DocumentService from "@/store/services/document.service.js";

export default {
  name: "EditInvoiceUploadForm",
  components: {
    GekkoButton,
    FileUploader,
    LoadingSpinner,
    CustomerSelect,
    PeriodSelector,
    InvoiceLines,
    PaymentTypeSelector
  },
  props: {
    transactionInvoice: {
      type: Object,
      default: () => {}
    },
    isEdit: {
      type: Boolean,
      default: false
    },
    emitSubmit: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      invoice: null,
      payment: null,
      selectedPaymentType: 'notpaid',

      created_new_invoice: null,

      invoiceFiles: null,
      uploadingFiles: false,

      errors: {},

      RETENTION_RATES: [15, 7, 2, 1, 0],

      fromInvoice: false,
      backupYear: null,

      maxTotalFileSize: 10,

      projectList: [],
      initialStatus: null,
      statusOptions: [
        { id: 'd', name: this.$gettext('Draft') },
        { id: 's', name: this.$gettext('Sent') },
        { id: 'p', name: this.$gettext('Paid') }
      ],
      loading: true,
      isSaving: false,
      isSubmitted: false,
      requiredFields: ['invoice_number', 'recipient_customer', 'invoice_payment-term', 'invoice_lines', 'invoice_file'],
      preselectedCustomer: null,
      defaultPayment: {
        amount: null,
        invoice: null,
        payment_date: null,
        payment_type: 'notpaid'
      }
    }
  },
  computed: {
    ...mapGetters({ 
      currentCompany: "main/getCurrentCompany",
      currentUserAccessRights: 'main/getCurrentAccessRights',
      companyTaxRates: "invoices/getCompanyTaxRates",
      storeInvoice: 'invoices/getInvoice',
      storeInvoicePayments: 'invoices/getInvoicePayments',
      isWaitingForResponse: 'main/isWaitingForResponse'
    }),
    totalFileSize () {
      let totalFileSize = 0
      if (this.invoiceFiles) {
        for (var i = 0; i < this.invoiceFiles.length; i++) {
          totalFileSize += this.invoiceFiles[i].size;
        }
      }
      return totalFileSize
    },
    invoiceTotalAmount () {
      let sum = 0
      if (this.invoice.invoice_lines) {
        for (var i = 0; i < this.invoice.invoice_lines.length; i++) {
          sum = sum + (this.invoice.invoice_lines[i].amount * this.invoice.invoice_lines[i].multiplier);
        }
        sum = Math.round(sum * 100) / 100; //make sure we have only 2 decimals
      }
      return sum
    },
    maxSizeExceeded () {
      return (this.totalFileSize > (this.maxTotalFileSize*1000*1000))
    },
    maxSizeExceededText () {
      return this.$gettext('Total size of files (%{ size }) exceeds upload limit. Please make sure your uploads do not exceed %{ max } MB.')
    },
    maxTotalSizeExceededText () {
      return this.$gettext('You\'ve used the maximum allowed storage of %{ max }. To upload new files, please delete some existing ones or contact us.')
    },
    isPaid () {
      return this.invoice.amount_to_be_paid == 0
    }
  },
  created () {
    this.backupYear = new Date().format('Y')
    const data = { 'sender_company': this.currentCompany.id, 'year': this.backupYear }

    this.getNextInvoiceNumber(data).then((response) => {
      this.setupData()
      if (!this.isEdit) {
        this.invoice.number = response
      }
      this.loading = false
    })
  },
  mounted () {
  },
  methods: {
    ...mapActions ({ 
      getNextInvoiceNumber: "invoices/getNextInvoiceNumber"
    }),
    ...mapMutations({
      setWaitingForResponse: 'main/SET_WAITING_FOR_RESPONSE'
    }),

    /**
     * Init, prepares and sets invoice and lines default data
     */
    setupData () {
      this.payment = this.defaultPayment

      if (this.isEdit && this.storeInvoice) {

        this.invoice = Object.assign({}, this.storeInvoice)

        this.preselectedCustomer = { name: '', customer_id: null, project_id: null }
        if (this.invoice.recipient_customer) {
          this.preselectedCustomer.customer_id = this.invoice.recipient_customer.id
          this.preselectedCustomer.name = this.invoice.recipient_customer.name
        }

        if (this.isPaid) {
          this.payment = Object.assign({}, this.storeInvoicePayments[0])
        }

        if (this.invoice.project) {
          this.invoice.project = this.invoice.project.id
        }

        this.initialStatus = this.invoice.status
      } else {
        const invoiceDate = new Date().format('Y-m-d')

        this.invoice = {
          paid: false,
          number: null,
          invoice_date: invoiceDate,
          payment_term: this.currentCompany.default_payment_term,
          sender_company: this.currentCompany.id,
          vat_reversed_charge: false,
          line_amount_is_gross: true,
          recipient_customer: null,
          invoice_lines: [],
          uploaded: true,
          currency: 'EUR',
          status: 'd'
        }
        
        this.invoice.retention_tax_percentage = this.RETENTION_RATES[this.RETENTION_RATES.length-1]
        if (this.currentCompany.companysettings.default_invoice_retention) {
            this.invoice.retention_tax_percentage = this.currentCompany.companysettings.default_invoice_retention
        }
      }
    },
    /**
     * Starts save process. Default submit form prevented
     */
    onSubmit (e = null) {
      if (!this.isSaving) {
        this.isSaving = true
        this.isSubmitted = true
        if (e) e.preventDefault()

        if (!this.validateLines()) {
          this.$store.commit('setAlert', {
            title: this.$gettext('Could not save'),
            text: this.$gettext('Please review invoice line amounts'),
            variant: 'danger',
            duration: 4
          })
          this.isSaving = false
          this.isSubmitted = false
          return
        }

        if ((!this.invoiceFiles || this.invoiceFiles.length < 1) && !this.isEdit) {
          this.$store.commit('setAlert', {
            title: this.$gettext('Upload invoice file'),
            text: this.$gettext('You must attach a file'),
            variant: 'warning',
            duration: 4
          })
          this.isSaving = false
          this.isSubmitted = false
          return
        }

        if (this.invoice.status === 'p' && this.payment.payment_type == 'notpaid' && !this.isEdit) {
          this.$store.commit('setAlert', {
            title: this.$gettext('Set payment type'),
            text: this.$gettext('You must select a payment type when marking the invoice as paid.'),
            variant: 'warning',
            duration: 4
          })
          this.isSaving = false
          this.isSubmitted = false
          return
        }

        this.setWaitingForResponse(true)
        DocumentService.getTotals({ company_profile: this.currentCompany.id }).then((response) => {
          if (this.isEdit) {
            this.editInvoice()
          } else {
            const uploaderSize = this.$refs['file-uploader'].getTotalFileSize()
            if ((response.data.document_sizes + uploaderSize) < response.data.documents_max_size) {
                this.createInvoice()
            } else {
              this.isSaving = false
              this.isSubmitted = false
              this.$store.commit('setAlert', {
                title: this.$gettext('You\'ve reached your storage limit.'),
                text: this.$gettextInterpolate(this.maxTotalSizeExceededText, { max: niceBytes(response.data.documents_max_size, 1024, true) }),
                variant: 'danger',
                duration: 4
              })
            }
          }
        })
      }
    },
    /**
     * Checks if a field is valid or empty/invalid if form is not yet submitted
     * 
     * @param {string} field field identifier on this.requiredFields array
     * @returns {boolean} false if field is invalid, null for valid (bootstrap :state prop contextual state)
     */
    isValid (field) {
      let isValid
      switch (field) {
        case 'recipient_customer':
          isValid = (this.invoice.recipient_customer && this.invoice.recipient_customer !== '') ? null : false
          break;
        case 'invoice_number':
          isValid = (this.invoice.number && this.invoice.number !== '' && !this.errors.number) ? null : false
          break;
        case 'invoice_payment-term':
          isValid = (this.invoice.paid ||
                    (this.invoice.payment_term !== undefined &&
                        this.invoice.payment_term !== null &&
                        this.invoice.payment_term !== '')) ? null : false
          break;
        case 'invoice_lines':
          isValid = true
          break;
        case 'invoice_file':
          isValid = this.invoiceFiles && this.invoiceFiles.length > 0
          break;
      }
      if (!this.isSubmitted) isValid = null
      return isValid
    },
    validateLines () {
      let valid_lines = true
      if (this.invoice.invoice_lines.length === 1 && this.invoice.invoice_lines[0].default) {
        valid_lines = false
      } else {
        this.invoice.invoice_lines.forEach(line => {
          if (!line.default && line.amount === '') {
            valid_lines = false
          }
        })
      }
      return valid_lines
    },
    /**
     * Checks validates this.requiredFields with isValid method
     * 
     * @returns {boolean} false if any field is empty or invalid
     */
    formIsValid () {
      let formErrors = []
      this.requiredFields.forEach(field => {
        formErrors.push(this.isValid(field))
      })
      return !formErrors.some((x) => x === false)
    },
    /**
     * Creates a paid invoice via InvoiceUploadService
     *    then creates a correction via CorrectionService
     *    then uploads any files via DocumentUpload
     *    after that redirects to corresponding page
     */
    createInvoice () {
      if (this.formIsValid()) {
        this.prepareInvoiceLines()

        let invoiceCopy = JSON.parse(JSON.stringify(this.invoice));
        invoiceCopy.total_amount = this.invoiceTotalAmount;
        invoiceCopy.pdf_file = this.invoice.pdf_file;
        if (!invoiceCopy.recipient_customer) {
          invoiceCopy.recipient_customer = this.currentCompany.id;
        } else {
          invoiceCopy.recipient_customer = invoiceCopy.recipient_customer.id;
        }

        InvoiceUploadService.upload(invoiceCopy).then((response) => {
          if (this.invoice.paid) {
            this.payment.amount = response.data.total_amount
            this.payment.invoice = response.data.id
            this.payment.payment_date = this.invoice.invoice_date

            InvoicePaymentsService.create(this.payment).then(() => {
              this.$emit('created', false)
            }).finally(() => {
              this.setWaitingForResponse(false)
            })
          } else {
            this.setWaitingForResponse(false)
            this.$emit('created', false)
          }
        }).catch((error) => {
          if (error.status === 400) {
            this.errors = error.data
          }
          this.setWaitingForResponse(false)
          this.isSaving = false
        })

      } else {
        this.isSaving = false
        this.setWaitingForResponse(false)
      }
    },
    editInvoice () {
      if (this.formIsValid()) {
        this.prepareInvoiceLines()
        this.invoice.total_amount = this.invoiceTotalAmount

        if (!this.invoice.recipient_customer) {
          this.invoice.recipient_customer = this.currentCompany.id
        } else {
          this.invoice.recipient_customer = this.invoice.recipient_customer.id
        }

        delete this.invoice.pdf_file

        InvoiceUploadService.update(this.invoice.id, this.invoice).then(() => {
          if (this.payment.id) {
            if (this.invoice.paid) {
              InvoicePaymentsService.update(this.payment.id, this.payment).then(() => {
                this.isSaving = false
                this.$emit('updated', false)
              }).finally(() => {
                this.setWaitingForResponse(false)
              })
            } else {
              this.setWaitingForResponse(false)
            }
          } else {
            if (this.invoice.paid) {
              this.payment.amount = this.invoice.total_amount
              this.payment.invoice = this.invoice.id
              this.payment.payment_date = this.invoice.invoice_date
              InvoicePaymentsService.create(this.payment).then(() => {
                this.isSaving = false
                this.$emit('updated', false)
              }).finally(() => {
                this.setWaitingForResponse(false)
              })
            } else {
              this.setWaitingForResponse(false)
            }
          }

        }).then(() => {
          this.isSaving = false
          this.setWaitingForResponse(false)
          this.$emit('updated', false)
        })
      } else {
        this.isSaving = false
        this.setWaitingForResponse(false)
      }
    },
    /**
     * Removes the last item from this.invoice.invoice_lines array
     * which is suposed to be just a placeholder
     * Also removes subtotal_amount amount from all invoice_lines (neccesary?)
     */
    prepareInvoiceLines () {
      this.invoice.invoice_lines.pop()
      // why this
      // for (var i = 0; i < this.invoice.invoice_lines.length; i++) {
      //   delete this.invoice.invoice_lines[i].subtotal_amount
      // }
    },
    /**
     * Gets Dropzone Files from fileUploader component and sets this.invoiceFiles
     * and also sets [0] as this.invoice.pdf_file
     */
    updateInvoiceFiles () {
      this.invoiceFiles = this.$refs['file-uploader'].getDropzoneFiles()
      if (this.invoiceFiles.length > 0) {
        this.invoice.pdf_file = this.invoiceFiles[0]
      }
    },
    /**
     * Alias for utils/niceBytes
     */
    niceBytes (size, base, unit = false) {
      return niceBytes(size, base, unit)
    },
    /**
     * Gets customer's project list when a customer is selected
     * if none, projectList is emptied and invoice.project set to null
     * also it will set the customer payment_term (if set)
     */
    selectedCustomer (selected) {
      if (selected) {
        CustomerService.retrieve(selected.customer_id, { extended: true }).then((response) => {
          this.invoice.recipient_customer = response.data

          if (this.preselectedCustomer) {
            this.preselectedCustomer = null
          } else {
            this.invoice.project = null
          }

          const customer_data = response.data
          // this.invoice.project = null
          this.projectList = customer_data.projects
          if (this.projectList.length > 0) {
            this.projectList.unshift({ name: this.$gettext('Nothing Selected'), id: null })
          }    
          // set customer's payment_term
          if (customer_data.default_payment_term && customer_data.default_payment_term != this.currentCompany.default_payment_term ) {
            this.invoice.payment_term = customer_data.default_payment_term
          } else {
            this.invoice.payment_term = this.currentCompany.default_payment_term
          }
        })
      } else {
        this.projectList.splice(0)
      }

    },
    /**
     * If date input has changed and its year is different than backupYear
     * will get new invoice number from API
     */
    updateDate () {
      const year = this.invoice.invoice_date.slice(0,4);
      const data = { 'sender_company': this.currentCompany.id, 'year': year }
      if (year !== this.backupYear) {
        this.getNextInvoiceNumber(data).then((response) => {
          this.invoice.number = response
          this.backupYear = year
        })
      }
    },
    updatePaymentType () {
      if (this.payment.payment_type !== 'notpaid') {
        this.invoice.paid = true

        if (this.payment.payment_type != 'notpaid' && this.invoice.status !== 'p') {
          this.invoice.status = 'p'

          this.$store.commit('setAlert', {
            title: this.$gettext('Paid invoice'),
            text: this.$gettext('If you have selected a payment type, the invoice status must remain paid.'),
            variant: "info",
            duration: 4
          })
        }
      } else {
        this.invoice.paid = false
      }
    },
    changePaymentStatus() {
      if (this.invoice.status === 'p' && this.payment.payment_type == 'notpaid' && !this.isEdit) {
        this.$store.commit('setAlert', {
          title: this.$gettext('Watch out!'),
          text: this.$gettext('Remember to select a payment type when you mark the invoice as paid.'),
          variant: "info",
          duration: 4
        })
      }
    }
  }
}
</script>

<style lang="scss">

</style>
